/*
  lab-cart.js
  The cart as envelope. Dependency free, loaded with defer.

  Two modes, one rendering layer:
  - Preview (window.LAB_DEMO_CART present, injected by the static
    renderer): state lives in memory, seeded from the demo payload, and
    form[data-lab-demo-add] submits are intercepted locally. Checkout
    stays disabled.
  - Live (no LAB_DEMO_CART, i.e. real Shopify): state mirrors Shopify's
    cart AJAX API. GET /cart.js seeds and re-syncs, POST /cart/add.js
    handles any form posting to /cart/add, POST /cart/change.js handles
    quantity and removal. On any AJAX failure the native form post runs
    instead, which Shopify always honours. Checkout becomes a real
    navigation to /checkout.

  The drawer opens on the 'lab:cart:open' event dispatched by lab-base.js
  and after every successful add.
*/
(function () {
  'use strict';

  var drawer = null;
  var scrim = null;
  var itemsEl = null;
  var emptyEl = null;
  var footerEl = null;
  var subtotalEl = null;
  var countLabelEl = null;
  var lineTemplate = null;
  var shippingEl = null;
  var shippingTextEl = null;
  var shippingFillEl = null;
  var upsellsEl = null;
  var upsellsRowEl = null;
  var upsellTemplate = null;
  var upsells = [];
  var freeShippingThreshold = 15000;
  var lastFocused = null;
  var open = false;
  var tickTimer = null;
  var busy = false;

  /* Preview when the static renderer injected demo state; live otherwise. */
  var PREVIEW = typeof window.LAB_DEMO_CART !== 'undefined';

  /* ------------------------------------------------------------------ */
  /* Data adapter.                                                       */
  /* TODO: the real implementation swaps this adapter for Shopify's      */
  /* /cart.js AJAX API: GET /cart.js to read state, POST /cart/add.js    */
  /* to add a variant, POST /cart/change.js for quantity changes and     */
  /* removal. Keep the method names and return shapes identical so the   */
  /* rendering layer below needs no changes.                             */
  /* ------------------------------------------------------------------ */
  var cartData = {
    lines: [],

    seed: function () {
      /* Accepts a bare array of lines or a /cart.js-shaped object with an
         items array, so this keeps working when the adapter swaps to the
         real GET /cart.js response. */
      var raw = window.LAB_DEMO_CART;
      var items = Array.isArray(raw) ? raw : raw && Array.isArray(raw.items) ? raw.items : null;
      if (!items) return;
      this.lines = items.map(normaliseLine).filter(Boolean);
    },

    find: function (key) {
      for (var i = 0; i < this.lines.length; i += 1) {
        if (this.lines[i].key === key) return this.lines[i];
      }
      return null;
    },

    add: function (line) {
      var existing = this.find(line.key);
      if (existing) {
        existing.quantity += line.quantity;
        return existing;
      }
      this.lines.push(line);
      return line;
    },

    setQuantity: function (key, quantity) {
      var line = this.find(key);
      if (!line) return;
      if (quantity < 1) {
        this.remove(key);
        return;
      }
      line.quantity = quantity;
    },

    remove: function (key) {
      this.lines = this.lines.filter(function (line) {
        return line.key !== key;
      });
    },

    itemCount: function () {
      return this.lines.reduce(function (total, line) {
        return total + line.quantity;
      }, 0);
    },

    subtotal: function () {
      return this.lines.reduce(function (total, line) {
        return total + line.price * line.quantity;
      }, 0);
    }
  };

  /* Accepts loose shapes from window.LAB_DEMO_CART or product JSON. */
  function normaliseLine(raw) {
    if (!raw) return null;
    var image = raw.image || raw.featured_image || '';
    if (image && typeof image === 'object') image = image.src || '';
    var quantity = parseInt(raw.quantity || raw.qty || 1, 10);
    if (!(quantity > 0)) quantity = 1;
    var key = raw.key || raw.variant_id || raw.id;
    if (key === undefined || key === null || key === '') return null;
    var variant = raw.variant || raw.variant_title || '';
    if (variant === 'Default Title') variant = '';
    return {
      key: String(key),
      title: raw.title || raw.product_title || 'Untitled piece',
      variant: variant,
      price: parseInt(raw.price, 10) || 0,
      quantity: quantity,
      image: image,
      url: raw.url || ''
    };
  }

  /* Prices are subunit integers, matching Liquid's money filter output. */
  function formatMoney(subunits) {
    var pounds = subunits / 100;
    var wholePence = subunits % 100 === 0;
    return '£' + pounds.toLocaleString('en-GB', {
      minimumFractionDigits: wholePence ? 0 : 2,
      maximumFractionDigits: wholePence ? 0 : 2
    });
  }

  /* ------------------------------------------------------------------ */
  /* Live adapter: Shopify's cart AJAX API. Lines keep the exact shape   */
  /* normaliseLine produces so the rendering layer never branches.       */
  /* ------------------------------------------------------------------ */

  function cdnResize(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function lineFromShopifyItem(item) {
    var variant = item.variant_title || '';
    if (variant === 'Default Title') variant = '';
    return {
      key: String(item.key),
      title: item.product_title || item.title || 'Untitled piece',
      variant: variant,
      price: item.final_price,
      quantity: item.quantity,
      image: item.image ? cdnResize(item.image, 200) : '',
      url: item.url || ''
    };
  }

  function shopifyCart(path, payload) {
    var options = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    if (payload) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(payload);
    }
    return window.fetch(path, options).then(function (response) {
      if (!response.ok) throw new Error('cart request failed: ' + response.status);
      return response.json();
    });
  }

  function refreshFromShopify() {
    return shopifyCart('/cart.js').then(function (cart) {
      cartData.lines = (cart.items || []).map(lineFromShopifyItem);
      return cart;
    });
  }

  /* Add a variant on the live store, then re-sync and open the envelope.
     The just-added key is recovered from the refreshed lines: Shopify
     line keys are "<variant id>:<hash>". */
  function liveAdd(variantId, quantity, fallbackForm) {
    return shopifyCart('/cart/add.js', {
      items: [{ id: variantId, quantity: quantity }]
    })
      .then(refreshFromShopify)
      .then(function () {
        var addedKey;
        for (var i = 0; i < cartData.lines.length; i += 1) {
          if (cartData.lines[i].key.indexOf(String(variantId) + ':') === 0) {
            addedKey = cartData.lines[i].key;
            break;
          }
        }
        render(addedKey);
        openDrawer();
      })
      .catch(function () {
        if (fallbackForm && typeof fallbackForm.submit === 'function') {
          fallbackForm.submit();
        }
      });
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */

  function render(justAddedKey) {
    if (!itemsEl) return;
    itemsEl.innerHTML = '';

    cartData.lines.forEach(function (line) {
      var node = lineTemplate.content.firstElementChild.cloneNode(true);
      node.setAttribute('data-line-key', line.key);

      var img = node.querySelector('[data-line-image]');
      if (line.image) {
        img.src = line.image;
        img.alt = line.title;
      } else {
        var thumb = node.querySelector('[data-line-thumb]');
        if (thumb) thumb.classList.add('lab-cart__thumb--blank');
        img.remove();
      }

      var link = node.querySelector('[data-line-title]');
      link.textContent = line.title;
      if (line.url) {
        link.href = line.url;
      } else {
        link.removeAttribute('href');
      }

      var variantEl = node.querySelector('[data-line-variant]');
      if (line.variant) {
        variantEl.textContent = line.variant;
      } else {
        variantEl.remove();
      }

      node.querySelector('[data-line-qty]').textContent = line.quantity;
      node.querySelector('[data-line-price]').textContent = formatMoney(line.price * line.quantity);

      if (justAddedKey && line.key === justAddedKey) {
        node.classList.add('is-just-added');
      }

      itemsEl.appendChild(node);
    });

    var hasLines = cartData.lines.length > 0;
    emptyEl.hidden = hasLines;
    footerEl.hidden = !hasLines;
    subtotalEl.textContent = formatMoney(cartData.subtotal());
    updateCounts();
    renderShipping();
    renderUpsells();

    if (justAddedKey) {
      window.clearTimeout(tickTimer);
      tickTimer = window.setTimeout(function () {
        var marked = itemsEl.querySelector('.is-just-added');
        if (marked) marked.classList.remove('is-just-added');
      }, 2000);
    }
  }

  /* Free-shipping progress. Threshold comes from the drawer root's
     data-free-shipping-threshold attribute (subunits, default 15000).
     Recomputed on every cart change because render() calls it. */
  function renderShipping() {
    if (!shippingEl || !shippingTextEl || !shippingFillEl) return;
    var hasLines = cartData.lines.length > 0;
    shippingEl.hidden = !hasLines;
    if (!hasLines) return;

    var subtotal = cartData.subtotal();
    var progress = Math.min(100, (subtotal / freeShippingThreshold) * 100);
    shippingFillEl.style.width = progress + '%';

    if (subtotal >= freeShippingThreshold) {
      shippingEl.classList.add('is-unlocked');
      shippingTextEl.textContent = 'Free UK delivery unlocked';
    } else {
      shippingEl.classList.remove('is-unlocked');
      var away = Math.ceil((freeShippingThreshold - subtotal) / 100);
      shippingTextEl.textContent =
        'You are £' + away.toLocaleString('en-GB') + ' away from free UK delivery';
    }
  }

  /* Upsell row. Hidden entirely when window.LAB_UPSELLS is absent (the
     real store, until it is wired) or when the cart is empty. */
  function renderUpsells() {
    if (!upsellsEl) return;
    upsellsEl.hidden = upsells.length === 0 || cartData.lines.length === 0;
  }

  function updateCounts() {
    var count = cartData.itemCount();
    var nodes = document.querySelectorAll('[data-lab-cart-count]');
    for (var i = 0; i < nodes.length; i += 1) {
      nodes[i].textContent = count;
    }
    /* The header hides the whole "(n)" wrapper at zero. */
    var wraps = document.querySelectorAll('[data-lab-cart-count-wrap]');
    for (var j = 0; j < wraps.length; j += 1) {
      wraps[j].hidden = count === 0;
    }
    if (countLabelEl) {
      countLabelEl.textContent = count === 1 ? '1 piece' : count + ' pieces';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Drawer open, close, focus trap                                      */
  /* ------------------------------------------------------------------ */

  function focusables() {
    var nodes = drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, function (node) {
      return node.offsetParent !== null || node === document.activeElement;
    });
  }

  function onKeydown(event) {
    if (!open) return;
    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusables();
    if (items.length === 0) {
      event.preventDefault();
      drawer.focus();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (event.shiftKey && (active === first || !drawer.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !drawer.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  function openDrawer() {
    if (!drawer || open) return;
    open = true;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    scrim.classList.add('is-open');
    document.documentElement.classList.add('lab-cart-lock');
    var closeButton = drawer.querySelector('[data-lab-cart-close]');
    if (closeButton) closeButton.focus();
    /* Live: re-sync on every open so adds made elsewhere (native posts,
       other tabs) show without a reload. */
    if (!PREVIEW) {
      refreshFromShopify()
        .then(function () { render(); })
        .catch(function () {});
    }
  }

  function closeDrawer() {
    if (!drawer || !open) return;
    open = false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    scrim.classList.remove('is-open');
    document.documentElement.classList.remove('lab-cart-lock');
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  /* ------------------------------------------------------------------ */
  /* Demo add to order                                                   */
  /* ------------------------------------------------------------------ */

  function lineFromForm(form) {
    var raw = form.getAttribute('data-product-json');
    if (!raw) return null;
    var product;
    try {
      product = JSON.parse(raw);
    } catch (error) {
      return null;
    }
    if (!product) return null;

    var data = new FormData(form);
    var variantId = data.get('id');
    var quantity = parseInt(data.get('quantity'), 10);
    if (!(quantity > 0)) quantity = 1;

    var variants = Array.isArray(product.variants) ? product.variants : [];
    var variant = null;
    if (variantId) {
      for (var i = 0; i < variants.length; i += 1) {
        if (String(variants[i].id) === String(variantId)) {
          variant = variants[i];
          break;
        }
      }
    }
    if (!variant) variant = product.selected_or_first_available_variant || variants[0] || null;

    /* The demo payloads built by lab-main-product.liquid and
       lab-product-card.liquid carry the 200w thumbnail under "image";
       full product drops carry "featured_image". Accept both. */
    var image = product.image || product.featured_image || '';
    if (image && typeof image === 'object') image = image.src || '';

    /* Key priority: matched variant, then the form's hidden id input
       (present on both the main product form and quick-add forms even when
       the JSON payload carries no variants array), then the product id or
       a payload-declared variant_id. Without this fallback the demo add
       resolves an empty key and normaliseLine drops the line silently. */
    var key = (variant && variant.id) || variantId || product.id || product.variant_id;

    return normaliseLine({
      key: key,
      title: product.title,
      variant: variant ? variant.title : (product.variant_title || ''),
      price: variant && variant.price !== undefined ? variant.price : product.price,
      quantity: quantity,
      image: image,
      url: product.url
    });
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.matches) return;
    var isDemoAdd = form.matches('form[data-lab-demo-add]');
    var action = String(form.getAttribute('action') || '');
    var isCartAdd = action.indexOf('/cart/add') !== -1;

    if (PREVIEW) {
      if (!isDemoAdd) return;
      event.preventDefault();
      var line = lineFromForm(form);
      if (!line) return;
      var added = cartData.add(line);
      render(added.key);
      openDrawer();
      return;
    }

    /* Live: intercept any add-to-cart post for an AJAX add so the
       envelope opens without a reload. liveAdd falls back to the native
       post on failure. */
    if (!isCartAdd && !isDemoAdd) return;
    var data = new FormData(form);
    var variantId = parseInt(data.get('id'), 10);
    var quantity = parseInt(data.get('quantity'), 10);
    if (!(quantity > 0)) quantity = 1;
    if (!variantId) return;
    event.preventDefault();
    liveAdd(variantId, quantity, form);
  }

  /* ------------------------------------------------------------------ */
  /* Upsells: a little something                                         */
  /* ------------------------------------------------------------------ */

  /* Builds the row once from window.LAB_UPSELLS
     ({handle, url, title, price, image, variant_id} each). */
  function seedUpsells() {
    if (!upsellsRowEl || !upsellTemplate) return;
    if (!Array.isArray(window.LAB_UPSELLS)) return;

    upsells = window.LAB_UPSELLS.filter(function (item) {
      return item && item.title && (item.variant_id || item.handle);
    });

    upsells.forEach(function (item, index) {
      var node = upsellTemplate.content.firstElementChild.cloneNode(true);
      node.setAttribute('data-upsell-index', index);

      var img = node.querySelector('[data-upsell-image]');
      if (item.image) {
        img.src = item.image;
        img.alt = item.title;
      } else {
        var thumb = node.querySelector('[data-upsell-thumb]');
        if (thumb) thumb.classList.add('lab-cart__upsell-thumb--blank');
        img.remove();
      }

      var link = node.querySelector('[data-upsell-title]');
      link.textContent = item.title;
      if (item.url) {
        link.href = item.url;
      } else {
        link.removeAttribute('href');
      }

      node.querySelector('[data-upsell-price]').textContent =
        formatMoney(parseInt(item.price, 10) || 0);

      var button = node.querySelector('[data-upsell-add]');
      button.setAttribute('aria-label', 'Add ' + item.title + ' to your order');

      upsellsRowEl.appendChild(node);
    });
  }

  /* One-click add reuses the demo add path: the same normalise, add and
     render(justAddedKey) sequence as form[data-lab-demo-add], so counts,
     totals and the shipping progress all update together. */
  function onUpsellClick(event) {
    var button = event.target.closest('[data-upsell-add]');
    if (!button) return;
    var itemEl = button.closest('[data-upsell-index]');
    if (!itemEl) return;
    var item = upsells[parseInt(itemEl.getAttribute('data-upsell-index'), 10)];
    if (!item) return;

    if (!PREVIEW && item.variant_id) {
      liveAdd(parseInt(item.variant_id, 10), 1, null);
      return;
    }

    var line = normaliseLine({
      key: item.variant_id || item.handle,
      title: item.title,
      price: item.price,
      quantity: 1,
      image: item.image,
      url: item.url
    });
    if (!line) return;
    var added = cartData.add(line);
    render(added.key);
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  function onDrawerClick(event) {
    var stepUp = event.target.closest('[data-line-increase]');
    var stepDown = event.target.closest('[data-line-decrease]');
    var remove = event.target.closest('[data-line-remove]');
    var control = stepUp || stepDown || remove;
    if (!control) return;
    var lineEl = control.closest('[data-line-key]');
    if (!lineEl) return;
    var key = lineEl.getAttribute('data-line-key');
    var line = cartData.find(key);
    if (!line) return;

    if (PREVIEW) {
      if (stepUp) {
        cartData.setQuantity(key, line.quantity + 1);
      } else if (stepDown) {
        cartData.setQuantity(key, line.quantity - 1);
      } else {
        cartData.remove(key);
      }
      render();
      return;
    }

    /* Live: one change in flight at a time; /cart/change.js with
       quantity 0 removes the line, matching the demo semantics. */
    if (busy) return;
    busy = true;
    var newQuantity = stepUp ? line.quantity + 1 : stepDown ? line.quantity - 1 : 0;
    if (newQuantity < 0) newQuantity = 0;
    shopifyCart('/cart/change.js', { id: key, quantity: newQuantity })
      .then(refreshFromShopify)
      .catch(function () {
        return refreshFromShopify().catch(function () {});
      })
      .then(function () {
        busy = false;
        render();
      });
  }

  function onDocumentClick(event) {
    if (event.target.closest('[data-lab-cart-close]')) {
      closeDrawer();
      return;
    }
    if (event.target.closest('[data-lab-cart-scrim]')) {
      closeDrawer();
    }
    /* Checkout: disabled button in preview; init() rewires it to a real
       /checkout navigation in live mode. Nothing to suppress here. */
  }

  function init() {
    drawer = document.querySelector('[data-lab-cart]');
    if (!drawer) return;
    scrim = document.querySelector('[data-lab-cart-scrim]');
    itemsEl = drawer.querySelector('[data-lab-cart-items]');
    emptyEl = drawer.querySelector('[data-lab-cart-empty]');
    footerEl = drawer.querySelector('[data-lab-cart-footer]');
    subtotalEl = drawer.querySelector('[data-lab-cart-subtotal]');
    countLabelEl = drawer.querySelector('[data-lab-cart-count-label]');
    lineTemplate = document.querySelector('[data-lab-cart-line-template]');
    if (!scrim || !itemsEl || !emptyEl || !footerEl || !subtotalEl || !lineTemplate) return;

    /* Phase 2 hooks. All optional so the drawer still works if a build
       ships without them. */
    shippingEl = drawer.querySelector('[data-lab-cart-shipping]');
    shippingTextEl = drawer.querySelector('[data-lab-cart-shipping-text]');
    shippingFillEl = drawer.querySelector('[data-lab-cart-shipping-fill]');
    upsellsEl = drawer.querySelector('[data-lab-cart-upsells]');
    upsellsRowEl = drawer.querySelector('[data-lab-cart-upsells-row]');
    upsellTemplate = document.querySelector('[data-lab-cart-upsell-template]');
    freeShippingThreshold = parseInt(drawer.getAttribute('data-free-shipping-threshold'), 10);
    if (!(freeShippingThreshold > 0)) freeShippingThreshold = 15000;

    if (PREVIEW) {
      var demoNote = drawer.querySelector('[data-lab-cart-preview-note]');
      if (demoNote) {
        demoNote.textContent = 'This is a design preview. Checkout is disabled.';
        demoNote.hidden = false;
      }
      cartData.seed();
    }
    seedUpsells();
    render();

    if (!PREVIEW) {
      /* Live: seed from the real cart, make checkout a real navigation
         and drop the preview-only notice. */
      refreshFromShopify()
        .then(function () { render(); })
        .catch(function () {});
      var checkoutButton = drawer.querySelector('[data-lab-cart-checkout]');
      if (checkoutButton) {
        checkoutButton.disabled = false;
        checkoutButton.addEventListener('click', function () {
          window.location.href = '/checkout';
        });
      }
      var previewNote = drawer.querySelector('[data-lab-cart-preview-note]');
      if (previewNote) previewNote.hidden = true;
    }

    itemsEl.addEventListener('click', onDrawerClick);
    if (upsellsRowEl) upsellsRowEl.addEventListener('click', onUpsellClick);
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('submit', onSubmit);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('lab:cart:open', openDrawer);
    window.addEventListener('lab:cart:open', openDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
